import { useCallback, useEffect, useRef, useState } from 'react'
import { interviewsApi } from '../services/api'

export type MockSessionState =
  | 'idle'
  | 'requesting_mic'
  | 'connecting'
  | 'listening'
  | 'speaking'
  | 'ending'
  | 'ended'
  | 'error'

export interface MockTranscriptEntry {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface MockInterviewSessionOptions {
  interviewId: string
  /** Called when the session ends (after transcript save). Caller should refetch interview to get feedback. */
  onEnded?: (args: { sessionId: string; durationSeconds: number; endedReason: string }) => void
  /** Called on unrecoverable error. Caller should show a toast. */
  onError?: (message: string) => void
}

const MAX_SESSION_SECONDS = 15 * 60 // 15 minute hard cap

// OpenAI Realtime occasionally emits non-fatal error events (e.g. cancelling
// a response that already completed, committing an empty audio buffer).
// These must NOT tear down the session — the peer connection is still
// healthy and the user can keep going. CrowCase sidesteps this by not
// handling the 'error' branch at all; we keep the branch for dev visibility
// but route benign patterns to console.warn instead of handleError.
const BENIGN_REALTIME_ERRORS = [
  /no active response/i,
  /response.*already\s*(completed|finished|done)/i,
  /response.*(in\s*progress|in-progress)/i,
  /input_audio_buffer.*empty/i,
]

// The session-start lambda returns structured errors as { error: <code>,
// message: <human> }, but apiCall surfaces the machine-readable `error` code
// (not the `message`) — so without this map the user would see a raw code like
// "voice_service_not_configured" (the AI Mock Interview "not responding" report,
// task #212). Translate the codes that endpoint can return into the human text
// it intends. String errors (e.g. "Only clients can start a mock interview")
// already arrive human and fall through the `raw` fallback below.
const START_ERROR_MESSAGES: Record<string, string> = {
  voice_service_not_configured: 'Voice service is not yet configured. Please try again later.',
  voice_service_error: 'Could not start the voice session. Please try again in a moment.',
  prep_guide_missing: 'Your prep guide has not been generated yet.',
  // MWA-283 — same prep-guide state the portal card describes, so it uses the
  // same word for the person doing the reviewing.
  prep_guide_unpublished: 'Your prep guide is still being reviewed by your strategist.',
  prep_guide_unread: 'Please review your prep guide before starting a mock interview.',
  interview_closed: 'Mock interviews are disabled for accepted offers.',
}

/**
 * Wraps the OpenAI Realtime API + WebRTC lifecycle for an AI Mock Interviewer
 * session. Rendering-only components can use this hook to drive UI without
 * worrying about mic, peer connection, audio element, or data-channel events.
 *
 * Adapted from CrowCase's widget.js voice flow.
 */
export function useMockInterviewSession(opts: MockInterviewSessionOptions) {
  const { interviewId, onEnded, onError } = opts

  const [state, setState] = useState<MockSessionState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<MockTranscriptEntry[]>([])
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [muted, setMuted] = useState(false)

  // Refs for all transient WebRTC resources. Using refs (not state) keeps the
  // render surface stable and avoids re-creating them on re-render.
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const micStreamRef = useRef<MediaStream | null>(null)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const dcRef = useRef<RTCDataChannel | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const startedAtRef = useRef<number | null>(null)
  const endedReasonRef = useRef<string>('completed')
  const endingRef = useRef<boolean>(false)
  const bargeInTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Pending user transcript chunks keyed by item_id, flushed on completion
  const pendingUserTranscriptRef = useRef<Map<string, string>>(new Map())
  // Tracks the assistant response currently streaming. Set on
  // `response.created`, cleared on `response.done` / `response.cancelled`.
  // Used to gate the barge-in cancel so we don't send `response.cancel` when
  // nothing is active — which would trigger OpenAI's
  // "Cancellation failed: no active response found" error event and
  // (previously) end the session on the first user utterance.
  const activeResponseIdRef = useRef<string | null>(null)
  // One-shot latch for the opening `response.create`. OpenAI emits both
  // `session.created` and `session.updated` during handshake; firing the
  // greeting on both would trigger a "response already in progress" error.
  // Matches CrowCase's `voiceGreetingSent` flag.
  const greetingSentRef = useRef<boolean>(false)
  // Latest-ref mirror of the `transcript` state. Needed because `dc.onmessage`
  // is registered once inside `startSession` and captures the initial closures
  // — a subsequent `setTranscript` update re-creates `saveTranscriptIfNeeded`,
  // but the data-channel listener still invokes the original, which reads
  // `transcript = []`. Without this ref the save Lambda receives
  // `transcriptLen: 0` even after a 3-minute session with dozens of turns
  // (confirmed in CloudWatch on 2026-04-17). Standard React "latest ref"
  // pattern, same shape already used by activeResponseIdRef / greetingSentRef.
  const transcriptRef = useRef<MockTranscriptEntry[]>([])
  // Tracks whether the assistant's audio is actively PLAYING, driven by the GA
  // WebRTC `output_audio_buffer.started/stopped/cleared` events. Audio keeps
  // playing for several seconds after generation completes (`response.done`),
  // so this — not `activeResponseIdRef` — is the correct signal for "the AI has
  // actually finished talking" when gating teardown and the speaking/listening UI.
  const audioPlayingRef = useRef<boolean>(false)

  const clearBargeInTimer = () => {
    if (bargeInTimerRef.current) {
      clearTimeout(bargeInTimerRef.current)
      bargeInTimerRef.current = null
    }
  }

  const stopElapsedTimer = () => {
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current)
      elapsedIntervalRef.current = null
    }
  }

  const cleanup = useCallback(() => {
    clearBargeInTimer()
    stopElapsedTimer()
    try {
      if (dcRef.current) {
        dcRef.current.onmessage = null
        dcRef.current.onopen = null
        dcRef.current.onerror = null
        dcRef.current.close()
      }
    } catch (err) {
      console.warn('[mock-interview] data channel cleanup error:', err)
    }
    dcRef.current = null

    try {
      if (pcRef.current) {
        pcRef.current.ontrack = null
        pcRef.current.onconnectionstatechange = null
        pcRef.current.oniceconnectionstatechange = null
        pcRef.current.close()
      }
    } catch (err) {
      console.warn('[mock-interview] peer connection cleanup error:', err)
    }
    pcRef.current = null

    try {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop())
      }
    } catch (err) {
      console.warn('[mock-interview] mic stream cleanup error:', err)
    }
    micStreamRef.current = null

    try {
      if (audioElRef.current) {
        audioElRef.current.srcObject = null
        audioElRef.current.pause()
        audioElRef.current.remove()
      }
    } catch (err) {
      console.warn('[mock-interview] audio element cleanup error:', err)
    }
    audioElRef.current = null

    endingRef.current = false
  }, [])

  const saveTranscriptIfNeeded = useCallback(
    async (reason: string) => {
      const sessionId = sessionIdRef.current
      if (!sessionId) return
      const startedAt = startedAtRef.current || Date.now()
      const durationSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000))
      try {
        // Read from transcriptRef (not the closed-over `transcript` state)
        // because dc.onmessage → handleRealtimeEvent → endSession was
        // registered once at startSession time and captures stale state.
        // The ref is updated synchronously alongside every setTranscript so
        // it always reflects the latest array.
        await interviewsApi.saveMockInterviewSession({
          interviewId,
          sessionId,
          transcript: transcriptRef.current,
          durationSeconds,
          endedReason: reason,
        })
      } catch (err) {
        console.error('[mock-interview] failed to save transcript:', err)
      }
      if (onEnded) onEnded({ sessionId, durationSeconds, endedReason: reason })
    },
    [interviewId, onEnded]
  )

  // Wait until the assistant has actually FINISHED PLAYING before proceeding.
  // Resolves when BOTH the response lifecycle is done (`activeResponseIdRef`
  // cleared on response.done/cancelled) AND the audio output buffer has drained
  // (`audioPlayingRef` cleared on output_audio_buffer.stopped/cleared), or when
  // the timeout hits. Gating on response.done alone cut the AI off mid-sentence
  // because audio plays for several seconds after generation completes
  // (Asana 1214200168864200; the GA output_audio_buffer tail measured ~8s).
  const waitForPlaybackDrain = (timeoutMs: number) =>
    new Promise<void>((resolve) => {
      const startedAt = Date.now()
      const poll = () => {
        if (!activeResponseIdRef.current && !audioPlayingRef.current) return resolve()
        if (Date.now() - startedAt > timeoutMs) return resolve()
        setTimeout(poll, 100)
      }
      poll()
    })

  const endSession = useCallback(
    async (reason: string = 'completed') => {
      if (endingRef.current) return
      endingRef.current = true
      endedReasonRef.current = reason
      setState('ending')
      // Let the AI finish its current statement before tearing down the peer
      // connection — wait for the audio output buffer to DRAIN, not just for
      // generation to finish. Skip the wait when the connection is already gone
      // (network_loss / dropped): no further events will fire, so waiting would
      // only stall the full cap. 15s cap is a safety net — the 15-min session
      // cap (MAX_SESSION_SECONDS) and per-response OpenAI limits already bound
      // how long the playback tail can be.
      if (pcRef.current?.connectionState === 'connected') {
        await waitForPlaybackDrain(15_000)
      }
      await saveTranscriptIfNeeded(reason)
      cleanup()
      setState('ended')
    },
    [saveTranscriptIfNeeded, cleanup]
  )

  const handleError = useCallback(
    (message: string) => {
      console.error('[mock-interview]', message)
      setErrorMessage(message)
      setState('error')
      cleanup()
      if (onError) onError(message)
    },
    [cleanup, onError]
  )

  // Handle events coming down the OpenAI Realtime data channel. Event types
  // follow https://platform.openai.com/docs/api-reference/realtime-server-events
  const handleRealtimeEvent = useCallback(
    (evt: unknown) => {
      const e = evt as { type: string; [k: string]: unknown }
      if (!e || typeof e.type !== 'string') return

      switch (e.type) {
        case 'session.created':
        case 'session.updated': {
          // One-shot opening greeting — OpenAI emits both `session.created`
          // and `session.updated` during handshake. Firing `response.create`
          // on both causes a "response already in progress" error that our
          // error handler (previously) treated as fatal.
          if (greetingSentRef.current) break
          if (dcRef.current?.readyState !== 'open') break
          greetingSentRef.current = true
          try {
            dcRef.current.send(JSON.stringify({ type: 'response.create' }))
          } catch (err) {
            console.warn('[mock-interview] failed to request initial response:', err)
            // Undo the latch so a subsequent session event can retry
            greetingSentRef.current = false
          }
          break
        }
        case 'input_audio_buffer.speech_started': {
          setState('listening')
          clearBargeInTimer()
          // Barge-in ONLY when the assistant is actively mid-response. In a
          // Q&A interview the user usually answers AFTER the AI finishes,
          // so there is nothing to cancel — sending `response.cancel`
          // anyway triggers OpenAI's "no active response found" error and
          // (before the fix) killed the session. Double-guarded like
          // CrowCase's widget.js — outer gate here, inner re-check inside
          // the timer in case the response completed during the debounce.
          //
          // Debounce raised 500ms → 1200ms (Asana 1214200259487697): the
          // 500ms window filters brief coughs/taps but was still killing
          // AI audio on sustained ambient noise (cars driving by, nearby
          // TV, HVAC bursts). 1200ms requires ~1s of continuous detected
          // speech before cancelling the AI — if it was genuine user
          // speech the user is still talking at t+1200, and if it was
          // noise `speech_stopped` arrives first and clears the timer.
          if (activeResponseIdRef.current) {
            bargeInTimerRef.current = setTimeout(() => {
              if (!activeResponseIdRef.current) return
              if (dcRef.current?.readyState !== 'open') return
              try {
                dcRef.current.send(JSON.stringify({ type: 'response.cancel' }))
              } catch {
                /* ignore */
              }
            }, 1200)
          }
          break
        }
        case 'input_audio_buffer.speech_stopped': {
          clearBargeInTimer()
          break
        }
        case 'conversation.item.input_audio_transcription.delta': {
          // Accumulate partial transcript per item_id
          const itemId = (e.item_id as string) || ''
          const delta = (e.delta as string) || ''
          if (itemId) {
            const prev = pendingUserTranscriptRef.current.get(itemId) || ''
            pendingUserTranscriptRef.current.set(itemId, prev + delta)
          }
          break
        }
        case 'conversation.item.input_audio_transcription.completed': {
          const itemId = (e.item_id as string) || ''
          const fullTranscript =
            (e.transcript as string) ||
            pendingUserTranscriptRef.current.get(itemId) ||
            ''
          if (itemId) pendingUserTranscriptRef.current.delete(itemId)
          if (fullTranscript.trim()) {
            setTranscript((prev) => {
              const next = [
                ...prev,
                { role: 'user' as const, content: fullTranscript.trim(), timestamp: new Date().toISOString() },
              ]
              transcriptRef.current = next
              return next
            })
          }
          break
        }
        case 'response.created': {
          // Track the active response id so the barge-in cancel can be
          // gated on "is the assistant actually speaking right now?"
          const responseId =
            ((e.response as { id?: string } | undefined)?.id) ?? 'active'
          activeResponseIdRef.current = responseId
          setState('speaking')
          break
        }
        case 'response.output_audio.delta': {
          setState('speaking')
          break
        }
        case 'response.output_audio.done': {
          // Generation of the audio finished — but PLAYBACK continues for
          // several seconds. Do NOT flip to 'listening' here; the
          // output_audio_buffer.stopped event (true end of playback) owns that
          // transition. (response.done clears activeResponseIdRef below.)
          break
        }
        case 'response.done':
        case 'response.cancelled': {
          // Authoritative end-of-response — safe to clear the tracked id
          // so a future `speech_started` won't send a cancel into the void.
          activeResponseIdRef.current = null
          // Stuck-state guard: if no audio is (still) playing — e.g. a
          // text-only response or a missed output_audio_buffer.stopped —
          // return to 'listening' now so the UI can't get stuck on 'speaking'.
          // When audio IS still playing, output_audio_buffer.stopped owns it.
          if (!audioPlayingRef.current) setState('listening')
          break
        }
        case 'output_audio_buffer.started': {
          // True start of audio PLAYBACK (GA WebRTC) — drives the 'speaking'
          // state and the teardown gate (see waitForPlaybackDrain).
          audioPlayingRef.current = true
          setState('speaking')
          break
        }
        case 'output_audio_buffer.stopped': {
          // Buffer fully drained — the AI has actually finished talking now.
          audioPlayingRef.current = false
          setState('listening')
          break
        }
        case 'output_audio_buffer.cleared': {
          // Buffer cut by a barge-in/interrupt or manual clear — clear the gate
          // so endSession won't wait on audio that will never finish playing.
          audioPlayingRef.current = false
          setState('listening')
          break
        }
        case 'response.output_audio_transcript.done': {
          const content = (e.transcript as string) || ''
          if (content.trim()) {
            setTranscript((prev) => {
              const next = [
                ...prev,
                {
                  role: 'assistant' as const,
                  content: content.trim(),
                  timestamp: new Date().toISOString(),
                },
              ]
              transcriptRef.current = next
              return next
            })
          }
          break
        }
        case 'response.function_call_arguments.done': {
          const name = (e.name as string) || ''
          const callId = (e.call_id as string) || ''
          // Acknowledge the function call so OpenAI's session state stays
          // consistent. Fire-and-forget — matches CrowCase's widget.js.
          // Cosmetic for our single-tool flow today; future-proofs adding
          // a second tool without OpenAI hanging on unacked calls.
          if (callId && dcRef.current?.readyState === 'open') {
            try {
              dcRef.current.send(
                JSON.stringify({
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: callId,
                    output: JSON.stringify({ success: true }),
                  },
                })
              )
            } catch {
              /* ignore */
            }
          }
          if (name === 'end_mock_interview') {
            let reason = 'completed'
            try {
              const args = JSON.parse((e.arguments as string) || '{}')
              if (args.reason) reason = String(args.reason)
            } catch {
              /* ignore */
            }
            // End gracefully — session save happens in endSession()
            endSession(reason).catch((err) => console.error(err))
          }
          break
        }
        case 'error': {
          const msg =
            (typeof e.error === 'object' && e.error
              ? ((e.error as { message?: string }).message ?? 'Realtime error')
              : 'Realtime error') || 'Realtime error'
          // Non-fatal: log and keep the session alive. Peer-connection
          // failures are handled separately by `pc.onconnectionstatechange`
          // so genuine transport loss still calls `endSession` below.
          if (BENIGN_REALTIME_ERRORS.some((re) => re.test(msg))) {
            console.warn('[mock-interview] non-fatal Realtime error (ignored):', msg)
            break
          }
          handleError(msg)
          break
        }
        default:
          // Many events (response.output_item.*, rate_limits.*, etc.) are not
          // actionable here. Log the type at debug level so the GA event-name
          // migration can be verified against real traffic without adding
          // warn/error noise.
          console.debug('[mock-interview] unhandled realtime event:', e.type)
          break
      }
    },
    [endSession, handleError]
  )

  const startSession = useCallback(async () => {
    if (state !== 'idle' && state !== 'error' && state !== 'ended') return

    setErrorMessage(null)
    setTranscript([])
    setElapsedSeconds(0)
    endingRef.current = false
    // Fresh session — clear lifecycle refs so a prior errored session
    // cannot leak state into a new one.
    activeResponseIdRef.current = null
    greetingSentRef.current = false
    transcriptRef.current = []

    // 1) Request mic permission first — users understand this gesture
    setState('requesting_mic')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      handleError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone permission denied. Please allow mic access and try again.'
          : 'Could not access your microphone.'
      )
      return
    }
    micStreamRef.current = stream

    // 2) Get ephemeral token from our backend
    setState('connecting')
    let tokenResp
    try {
      tokenResp = await interviewsApi.startMockInterviewSession(interviewId)
    } catch (err) {
      const raw = err instanceof Error ? err.message : ''
      handleError(START_ERROR_MESSAGES[raw] || raw || 'Could not start mock interview session')
      return
    }
    sessionIdRef.current = tokenResp.sessionId
    startedAtRef.current = Date.now()

    // 3) Set up the RTCPeerConnection
    const pc = new RTCPeerConnection()
    pcRef.current = pc

    // Audio playback element for the AI voice
    const audioEl = document.createElement('audio')
    audioEl.autoplay = true
    audioEl.setAttribute('playsinline', 'true')
    audioElRef.current = audioEl

    pc.ontrack = (evt) => {
      if (audioElRef.current && evt.streams[0]) {
        audioElRef.current.srcObject = evt.streams[0]
      }
    }

    pc.onconnectionstatechange = () => {
      const s = pc.connectionState
      if (s === 'failed' || s === 'disconnected') {
        // Save whatever transcript we have and end cleanly
        endSession('network_loss').catch((err) => console.error(err))
      }
    }

    stream.getAudioTracks().forEach((track) => {
      pc.addTrack(track, stream)
    })

    // Data channel for JSON events
    const dc = pc.createDataChannel('oai-events')
    dcRef.current = dc
    dc.onmessage = (evt) => {
      try {
        const parsed = JSON.parse(evt.data)
        handleRealtimeEvent(parsed)
      } catch (err) {
        console.warn('[mock-interview] could not parse data channel message:', err)
      }
    }

    // SDP offer/answer with OpenAI
    try {
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      const sdpResponse = await fetch(
        `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(tokenResp.model)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokenResp.ephemeralToken}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp || '',
        }
      )
      if (!sdpResponse.ok) {
        const body = await sdpResponse.text()
        throw new Error(`OpenAI SDP handshake failed (${sdpResponse.status}): ${body.slice(0, 200)}`)
      }
      const answerSdp = await sdpResponse.text()
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
    } catch (err) {
      handleError(
        err instanceof Error ? err.message : 'Could not establish voice connection'
      )
      return
    }

    setState('listening')

    // Start the elapsed-time timer
    elapsedIntervalRef.current = setInterval(() => {
      const startedAt = startedAtRef.current || Date.now()
      const secs = Math.floor((Date.now() - startedAt) / 1000)
      setElapsedSeconds(secs)
      if (secs >= MAX_SESSION_SECONDS && !endingRef.current) {
        endSession('time_limit').catch((err) => console.error(err))
      }
    }, 1000)
  }, [state, interviewId, handleRealtimeEvent, handleError, endSession])

  const toggleMute = useCallback(() => {
    if (!micStreamRef.current) return
    const next = !muted
    micStreamRef.current.getAudioTracks().forEach((t) => {
      t.enabled = !next
    })
    setMuted(next)
  }, [muted])

  // Ensure resources are released if the host component unmounts mid-session
  useEffect(() => {
    return () => {
      // Do NOT call endSession here — saves would fire from an unmounted
      // component. Just tear down WebRTC/mic resources.
      cleanup()
    }
  }, [cleanup])

  return {
    state,
    errorMessage,
    transcript,
    elapsedSeconds,
    muted,
    startSession,
    endSession,
    toggleMute,
  }
}
